<template>
  <div v-if="value" class="al-backdrop" @click.self="$emit('input', false)">
    <aside class="al-panel" role="dialog" aria-modal="true" aria-labelledby="ch-title">
      <div class="al-head">
        <div>
          <h5 id="ch-title" class="mb-0"><i class="fe fe-archive mr-1"></i> Previous claims</h5>
          <small class="text-muted">{{ subtitle }}</small>
        </div>
        <button class="btn btn-sm btn-link text-secondary" @click="$emit('input', false)" aria-label="Close"><i class="fe fe-x"></i></button>
      </div>

      <div class="al-tools" v-if="summary">
        <div class="ch-figure"><span>Earlier claims</span><b>{{ summary.claims }}</b></div>
        <div class="ch-figure"><span>Claimed</span><b>{{ money(summary.claimed) }}</b></div>
        <div class="ch-figure"><span>Approved</span><b>{{ money(summary.approved) }}</b></div>
        <div class="ch-figure"><span>Facilities</span><b>{{ summary.facilities }}</b></div>
        <button class="btn btn-sm btn-outline-secondary ml-auto align-self-center" @click="load" :disabled="loading" title="Reload"><i class="fe fe-refresh-cw"></i></button>
      </div>
      <div class="px-3 pt-2" v-if="summary && summary.limited_to_facility">
        <small class="text-muted">Only claims from your facility are listed.</small>
      </div>

      <div class="al-body">
        <div v-if="loading" class="text-center text-muted py-5"><span class="spinner-border spinner-border-sm mr-2"></span>Loading…</div>
        <div v-else-if="error" class="alert alert-danger small">{{ error }}</div>
        <div v-else-if="!claims.length" class="text-center text-muted py-5"><i class="fe fe-inbox d-block mb-2" style="font-size: 1.6rem;"></i>No claims found.</div>
        <ul v-else class="al-list">
          <li v-for="c in claims" :key="c.id" class="al-item">
            <span class="al-dot" :class="dotClass(c)"><i class="fe" :class="c.current ? 'fe-map-pin' : 'fe-file-text'"></i></span>
            <div class="al-content">
              <div class="d-flex flex-wrap align-items-baseline" style="gap: 6px;">
                <b v-if="c.current">{{ c.claim_unique_id || '#' + c.id }} <span class="badge badge-light">this claim</span></b>
                <router-link v-else :to="'/claim/' + c.id" target="_blank"><b>{{ c.claim_unique_id || '#' + c.id }}</b></router-link>
                <span class="badge" :class="'badge-soft-' + status(c).variant">{{ status(c).label }}</span>
              </div>
              <div class="small text-muted">
                {{ c.date | moment("D MMM YYYY") }}<span v-if="c.facility"> · {{ c.facility }}</span>
              </div>
              <div class="small mt-1" v-if="c.diagnosis"><i class="fe fe-thermometer mr-1 text-muted"></i>{{ c.diagnosis }}</div>
              <div class="small mt-1">
                Claimed <b>{{ money(c.claimed) }}</b>
                <template v-if="c.status === 'approved'"> · approved <b class="text-success">{{ money(c.approved) }}</b></template>
                <template v-else-if="c.vetted"> · vetted {{ money(c.vetted) }}</template>
                <template v-if="c.authorization_code"> · code {{ c.authorization_code }}</template>
              </div>
              <button v-if="c.items.length" class="btn btn-link btn-sm p-0 mt-1" @click="toggle(c.id)">
                {{ open[c.id] ? "Hide items" : "Show " + c.items.length + " item(s)" }}
              </button>
              <ul v-if="open[c.id]" class="al-changes small mt-1">
                <li v-for="l in c.items" :key="l.id" class="d-flex justify-content-between" style="gap: 8px;">
                  <span>
                    <span class="text-muted">{{ l.type === 'drug' ? 'Drug' : 'Service' }}:</span> {{ l.name }}
                    <span v-if="l.quantity > 1" class="text-muted">× {{ l.quantity }}</span>
                    <span v-if="l.remark" class="text-danger"> · {{ l.remark }}</span>
                  </span>
                  <span class="text-nowrap">
                    {{ money(l.cost) }}<template v-if="l.approved !== null"> → <b>{{ money(l.approved) }}</b></template>
                  </span>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </div>
    </aside>
  </div>
</template>

<script>
import { getClaimStatus } from "@/utils/claimStatus";

/**
 * Slide-in list of the patient's claims (GET claims/{id}/patient-history), newest first with each claim's items,
 * opened from the claim page so reviewers can check earlier claims without leaving it.
 */
export default {
  name: "ClaimHistoryPanel",
  props: {
    value: { type: Boolean, default: false },
    claimId: { type: [Number, String], required: true },
    subtitle: { type: String, default: "" },
  },
  data() {
    return { claims: [], summary: null, loading: false, error: "", open: {} };
  },
  watch: {
    value(isOpen) { if (isOpen) this.load(); },
    claimId() { this.claims = []; this.summary = null; if (this.value) this.load(); },
  },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      this.axios.get(`/api/v1/auth/claims/${this.claimId}/patient-history`)
        .then((res) => {
          const data = res.data.data || {};
          this.claims = data.claims || [];
          this.summary = data.summary || null;
          this.open = {};
        })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load the previous claims."; })
        .finally(() => { this.loading = false; });
    },
    toggle(id) { this.$set(this.open, id, !this.open[id]); },
    status(c) { return getClaimStatus({ claim_status: c.status }); },
    dotClass(c) {
      if (c.current) return "dot-move";
      if (c.status === "approved") return "dot-new";
      if (c.status === "rejected") return "dot-del";
      return "";
    },
    money(v) { return "₦" + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }); },
  },
};
</script>

<style scoped>
.al-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.35); z-index: 1060; display: flex; justify-content: flex-end; }
.al-panel { background: #fff; width: 100%; max-width: 560px; height: 100%; display: flex; flex-direction: column; box-shadow: -10px 0 40px rgba(15, 23, 42, 0.2); }
.al-head { display: flex; justify-content: space-between; align-items: flex-start; padding: 18px 20px 12px; border-bottom: 1px solid #e2e8f0; }
.al-tools { display: flex; flex-wrap: wrap; gap: 18px; padding: 10px 20px; border-bottom: 1px solid #f1f5f9; }
.ch-figure span { display: block; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8; font-weight: 600; }
.ch-figure b { font-size: 0.95rem; color: #1e293b; }
.al-body { overflow-y: auto; padding: 16px 20px; flex: 1; }
.al-list { list-style: none; padding: 0; margin: 0; position: relative; }
.al-list::before { content: ""; position: absolute; left: 14px; top: 6px; bottom: 6px; width: 2px; background: #e2e8f0; }
.al-item { display: flex; gap: 12px; padding: 0 0 18px; position: relative; }
.al-dot { width: 30px; height: 30px; border-radius: 50%; background: #e0e7ff; color: #3730a3; display: flex; align-items: center; justify-content: center; flex-shrink: 0; z-index: 1; }
.al-dot.dot-new { background: #d1fae5; color: #065f46; }
.al-dot.dot-del { background: #fee2e2; color: #991b1b; }
.al-dot.dot-move { background: #ede9fe; color: #5b21b6; }
.al-content { flex: 1; min-width: 0; }
.al-changes { list-style: none; padding: 6px 10px; margin: 0; background: #f8fafc; border-radius: 8px; word-break: break-word; }
.badge-soft-success { background: #d1fae5; color: #065f46; }
.badge-soft-danger { background: #fee2e2; color: #991b1b; }
.badge-soft-warning { background: #fef3c7; color: #92400e; }
.badge-soft-info { background: #dbeafe; color: #1d4ed8; }
.badge-soft-dark { background: #e5e7eb; color: #1f2937; }
.badge-soft-indigo { background: #e0e7ff; color: #3730a3; }
.badge-soft-forwarded { background: #ede9fe; color: #5b21b6; }
.badge-soft-sent-back { background: #ffedd5; color: #9a3412; }
@media (max-width: 576px) { .al-panel { max-width: none; } }
</style>
